'use client'

import './app-gate.css'
import Link from 'next/link'
import { useParams } from 'next/navigation'
import { useTranslation } from 'react-i18next'
import { APP_UI } from '@/components/headers/HeaderPreview'
import { conOrigen } from '@/lib/origen-suscripcion'
import { useAppIntro } from '@/components/apps/AppIntro'

/**
 * Pantalla que ve quien todavia no tiene acceso a una app.
 *
 * Por que existe: las 11 apps con gate hacian router.replace a /{locale}/login en
 * cuanto el chequeo fallaba. Google ejecuta el JS, veia el login y registraba la URL
 * como "Pagina con redireccion"; peor todavia, como TODAS terminaban en el mismo
 * cascaron, eligio una sola canonica para el grupo (para /de/apps/acordes-vecinos
 * eligio /de/apps/3d-book, y la pagina se declaraba canonica de /de/login). Entre eso
 * y "Rastreada: actualmente sin indexar" quedaban afuera del indice 84 de las 112 URLs
 * de /apps.
 *
 * El gate NO se aflojo: la app se sigue montando solo con accessState 'allowed'. Lo
 * unico que cambia es que el "denied" se queda en su propia URL y muestra de que se
 * trata y como conseguirla, que es el patron que ya usaban bcm-nodes y vueltas.
 *
 * Los textos salen de APP_UI[appKey].i18n (message / cta), que ya existian para el modal
 * de la grilla y estan en los 7 idiomas. Las apps de suscripcion comparten uno solo
 * (apps:gate.subscription_*, ver SUSCRIPCION en HeaderPreview.jsx): el texto habla de
 * la suscripcion, no de la app, asi que es el mismo para todas.
 *
 * Este es el UNICO cartel: bcm-nodes y vueltas tenian cada una el suyo
 * escrito a mano, con su propio texto y el boton rojo. Si hay que cambiar el copy, se
 * cambia en apps.json y cambia en todas.
 *
 * reason: 'login' es el unico valor que hace algo, agrega la linea "¿ya lo compraste? /
 * ¿ya sos suscriptor?". Cualquier otro ('product', 'subscription') significa que la
 * sesion existe pero el acceso no.
 *
 * El titulo es el NOMBRE DE LA APP. Antes las 20 pantallas decian "Contenido bloqueado.",
 * que ademas de sonar agresivo (Brian, 20-sep-2026) les daba a todas el mismo h1, justo
 * al grupo de URLs que Google ya habia colapsado en una sola canonica.
 */
export default function AppAccessGate({ appKey, reason = 'product' }) {
  const { t } = useTranslation()
  const params = useParams()
  const locale = params?.locale || 'es'
  const ui = APP_UI?.[appKey]
  // Que hace la app, debajo del cartel (lib/apps/intros.js). Va AFUERA de la tarjeta y
  // despues de ella a proposito: la tarjeta es el cartel que escribio Brian y el boton
  // de compra no se mueve de lugar ni queda mas abajo.
  const intro = useAppIntro()

  // i18next devuelve la CLAVE cuando no encuentra el texto, asi que una clave mal escrita
  // en APP_UI se veia en pantalla como "vecinos.lock_message". Habia 20 asi. Esta red evita
  // que la proxima se publique: sin texto, no se dibuja nada.
  const tr = (key, fallback = '') => {
    if (!key) return fallback
    const valor = t(key)
    const sinNs = key.replace(/^apps:/, '')
    return valor && valor !== key && valor !== sinNs ? valor : fallback
  }

  const title = tr(ui?.nameKey, t('apps:gate.access_denied'))
  const message = tr(ui?.i18n?.message)
  const cta = tr(ui?.i18n?.cta, null)
  // A /subscribe va con la app de origen (lib/origen-suscripcion.js); el checkout de libros no cambia.
  const buyHref = ui?.buyPath ? conOrigen(`/${locale}${ui.buyPath}`, `app:${appKey}`) : null

  return (
    <div className="app-gate">
      <div className="app-gate-card">
        <h1 className="app-gate-title">{title}</h1>
        {message ? <p className="app-gate-message">{message}</p> : null}

        {buyHref && cta ? (
          <Link href={buyHref} className="app-gate-cta">
            {cta}
          </Link>
        ) : null}

        {/* Cuando el corte fue por falta de sesion, el que ya compro solo necesita
            entrar. El link va explicito para que tambien sea rastreable. A las apps de
            suscripcion no se les pregunta "¿ya lo compraste?": no se compran. */}
        {reason === 'login' ? (
          <p className="app-gate-login">
            {t(ui?.accessMode === 'subscription' ? 'apps:gate.login_prompt_subscriber' : 'apps:gate.login_prompt')}{' '}
            <Link href={`/${locale}/login?continue=${encodeURIComponent(`/${locale}${ui?.href || '/apps'}`)}`}>
              {t('apps:gate.login_cta')}
            </Link>
          </p>
        ) : null}

        <p className="app-gate-back">
          <Link href={`/${locale}/apps`}>{t('apps:gate.back_to_apps')}</Link>
        </p>
      </div>

      {intro?.parrafos?.length ? (
        <div className="app-gate-intro">
          {intro.parrafos.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      ) : null}
    </div>
  )
}
